import { API_BASE } from '../config.js';
import type { Car } from '@multirx/shared';
import type { CarBackupFile } from '@multirx/shared/game/garageBackup.js';
import { bumpCarGlbEpoch } from './carOrientationFix.js';

export async function fetchCars(limit?: number): Promise<Car[]> {
  const url = limit != null ? `${API_BASE}/api/cars?limit=${limit}` : `${API_BASE}/api/cars`;
  const res = await fetch(url);
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error((err as { error?: string }).error ?? 'Erreur lors du chargement des voitures');
  }
  return res.json() as Promise<Car[]>;
}

export async function fetchCarById(id: number): Promise<Car> {
  const res = await fetch(`${API_BASE}/api/cars/${id}`);
  if (!res.ok) {
    if (res.status === 404) throw new Error('Voiture non trouvée');
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error((err as { error?: string }).error ?? 'Erreur lors du chargement de la voiture');
  }
  return res.json() as Promise<Car>;
}

export async function createCar(car: Omit<Car, 'id'>): Promise<Car> {
  const res = await fetch(`${API_BASE}/api/cars`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(car),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(
      (err as { error?: string }).error ?? 'Erreur lors de la création de la voiture'
    );
  }
  return res.json() as Promise<Car>;
}

export async function updateCar(id: number, car: Omit<Car, 'id'>): Promise<Car> {
  const res = await fetch(`${API_BASE}/api/cars/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(car),
  });
  if (!res.ok) {
    if (res.status === 404) throw new Error('Voiture non trouvée');
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error((err as { error?: string }).error ?? 'Erreur lors de la mise à jour');
  }
  return res.json() as Promise<Car>;
}

/** Supprime une voiture (admin). */
export async function deleteCar(id: number): Promise<void> {
  const res = await fetch(`${API_BASE}/api/cars/${id}`, { method: 'DELETE' });
  if (!res.ok) {
    if (res.status === 404) throw new Error('Voiture non trouvée');
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error((err as { error?: string }).error ?? 'Erreur lors de la suppression');
  }
}

/** Envoie l'image d'une voiture (multipart). Retourne la voiture mise à jour avec imagePath. */
export async function uploadCarImage(id: number, file: File): Promise<Car> {
  const formData = new FormData();
  formData.append('image', file);
  const res = await fetch(`${API_BASE}/api/cars/${id}/image`, {
    method: 'POST',
    body: formData,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error((err as { error?: string }).error ?? "Erreur lors de l'upload de l'image");
  }
  return res.json() as Promise<Car>;
}

/** Associe une image de la bibliothèque d'assets (chemin public /assets/...). */
export async function assignCarImageFromLibrary(id: number, imagePath: string): Promise<Car> {
  const res = await fetch(`${API_BASE}/api/cars/${id}/image-path`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ imagePath }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error((err as { error?: string }).error ?? "Erreur lors de l'assignation de l'image");
  }
  return res.json() as Promise<Car>;
}

/** Envoie un fichier .glb comme modèle 3D personnalisé d'une voiture. */
export async function uploadCarModel3d(id: number, file: File): Promise<Car> {
  const formData = new FormData();
  formData.append('model', file);
  const res = await fetch(`${API_BASE}/api/cars/${id}/model3d`, {
    method: 'POST',
    body: formData,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error((err as { error?: string }).error ?? "Erreur lors de l'upload du modèle 3D");
  }
  return res.json() as Promise<Car>;
}

export interface Car3dModelItem {
  filename: string;
  publicPath: string;
  slug: string;
}

/** Catalogue des modèles 3D voiture disponibles (Kenney Car Kit). */
export async function fetchCar3dModels(): Promise<Car3dModelItem[]> {
  const res = await fetch(`${API_BASE}/api/car-models-3d`);
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(
      (err as { error?: string }).error ?? 'Erreur lors du chargement des modèles 3D'
    );
  }
  return res.json() as Promise<Car3dModelItem[]>;
}

/**
 * Tourne définitivement un modèle 3D du catalogue (fichier `.glb` sur disque) de
 * 90/180/270° autour de son axe vertical — voir l'utilitaire de recadrage
 * `carOrientationTool.ts`. Contrairement à `assignCarModel3dPath`, ceci modifie le
 * fichier source lui-même, pas juste l'association d'une voiture.
 */
export async function rotateCarModel3d(slug: string, degrees: 90 | 180 | 270): Promise<void> {
  const res = await fetch(`${API_BASE}/api/car-models-3d/${encodeURIComponent(slug)}/rotate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ degrees }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error((err as { error?: string }).error ?? 'Erreur lors de la rotation du modèle');
  }
  bumpCarGlbEpoch();
}

/**
 * Associe un modèle 3D du catalogue à une voiture (chemin public /assets/...).
 * `model3dPath: null` retire l’assignation manuelle (aperçu générique uniquement).
 */
export async function assignCarModel3dPath(id: number, model3dPath: string | null): Promise<Car> {
  const res = await fetch(`${API_BASE}/api/cars/${id}/model3d-path`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ model3dPath }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(
      (err as { error?: string }).error ?? "Erreur lors de l'assignation du modèle 3D"
    );
  }
  return res.json() as Promise<Car>;
}

export interface ImportCarsResult {
  imported: number;
  cars: Car[];
}

export async function importCarsBackup(payload: CarBackupFile): Promise<ImportCarsResult> {
  const res = await fetch(`${API_BASE}/api/cars/import`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error((err as { error?: string }).error ?? 'Erreur lors de l’import des voitures');
  }
  return res.json() as Promise<ImportCarsResult>;
}
